import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, todayET } from '../utils/formatters';

// Shipping — upload the TikTok "Shipping label + Packing slip" PDF for a batch
// of break orders and split it into a Hitters PDF and a Skunks PDF. All the
// matching happens server-side (routes/shipping.js); this page picks the
// scope, uploads, and shows the reconciliation so a bad split can't slip by.

function Stat({ label, value, sub, tone }) {
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'success' ? 'var(--success)' : 'var(--text)';
  return (
    <div style={{ flex: '1 1 140px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-card)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}

// Inline label + control pairs. The global form styles make labels small block
// captions and inputs full width, which is wrong for radios and checkboxes.
const inlineLabel = { display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, fontSize: 13, fontWeight: 400, color: 'var(--text)', textTransform: 'none', whiteSpace: 'nowrap' };
const inlineInput = { width: 'auto', margin: 0 };

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Checks the file is downloadable first (so our JSON errors still show), then
// lets the browser download it directly. Pulling a large label PDF into the
// page as a blob was dropping the connection partway through.
async function downloadFile(batchId, kind) {
  const path = `/api/shipping/batches/${batchId}/${kind}`;
  try {
    await api.get(path, { params: { check: 1 } });
  } catch (err) {
    let msg;
    if (!err.response) {
      msg = `Download failed: no response from the server (${err.message || 'network error'})`;
    } else {
      msg = err.response.data?.error || `Download failed (HTTP ${err.response.status})`;
    }
    alert(msg);
    return;
  }
  const a = document.createElement('a');
  a.href = `${api.defaults.baseURL || ''}${path}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function BatchReport({ batch, onAcknowledged }) {
  const [filter, setFilter] = useState('all');
  const [ackChecked, setAckChecked] = useState(false);
  const [acking, setAcking] = useState(false);
  const r = batch.report;
  const flags = r.issues || [];
  const locked = flags.length > 0 && !batch.acknowledged_at;
  const failedChecks = (r.checks || []).filter(c => !c.ok);

  const acknowledge = async () => {
    setAcking(true);
    try {
      const { data } = await api.post(`/api/shipping/batches/${batch.id}/acknowledge`);
      onAcknowledged(data);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not save the acknowledgement');
    } finally {
      setAcking(false);
    }
  };

  const rows = (r.customers || []).filter(c => filter === 'all' || c.pile === filter);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div className="card-title" style={{ margin: 0 }}>{batch.original_filename}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {batch.scope_label} · uploaded {formatDateTime(batch.created_at)}{batch.uploaded_by_name ? ` by ${batch.uploaded_by_name}` : ''}
        </div>
      </div>

      {flags.length === 0 ? (
        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius)', background: '#c6f6d5', color: '#22543d', fontWeight: 600 }}>
          ✓ Everything reconciles: {r.totals.labels} labels in, {r.hitters.labels} Hitters + {r.skunks.labels} Skunks, and the skunks match the skunk list ({r.skunkList.count}).
        </div>
      ) : (
        <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 'var(--radius)', background: '#fed7d7', color: '#9b2c2c', border: '2px solid var(--danger)' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
            ⚠ {plural(flags.length, 'problem')} found. Check these before printing.
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
            {flags.map((f, i) => <li key={i} style={{ marginBottom: 2 }}>{f.message}</li>)}
          </ul>
          {batch.acknowledged_at ? (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Reviewed by {batch.acknowledged_by_name || 'someone'} on {formatDateTime(batch.acknowledged_at)}.
            </div>
          ) : (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ ...inlineLabel, fontWeight: 600, color: '#9b2c2c' }}>
                <input type="checkbox" style={inlineInput} checked={ackChecked} onChange={e => setAckChecked(e.target.checked)} />
                I've reviewed these problems
              </label>
              <button className="btn btn-secondary" disabled={!ackChecked || acking} onClick={acknowledge}>
                {acking ? 'Saving…' : 'Unlock downloads'}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <Stat label="Labels ingested" value={r.totals.labels} sub={`${plural(r.totals.customers, 'customer')} · ${r.totals.pages} pages`} />
        <Stat label="Hitters" value={r.hitters.labels} sub={`labels · ${plural(r.hitters.customers, 'customer')}`} tone="success" />
        <Stat label="Skunks" value={r.skunks.labels} sub={`labels · ${plural(r.skunks.customers, 'customer')}`} />
        <Stat label="Skunk list" value={r.skunkList.count}
          sub={r.skunkList.count === r.skunks.customers ? 'matches skunks in PDF' : `${r.skunks.customers} skunks in PDF`}
          tone={r.skunkList.count === r.skunks.customers ? undefined : 'danger'} />
      </div>

      <div style={{ marginTop: 14 }}>
        {(r.checks || []).map((c, i) => (
          <div key={i} style={{ fontSize: 13, display: 'flex', gap: 8, padding: '2px 0', color: c.ok ? 'var(--text)' : 'var(--danger)', fontWeight: c.ok ? 400 : 600 }}>
            <span style={{ width: 16 }}>{c.ok ? '✓' : '✗'}</span>
            <span>{c.label}</span>
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>— {c.detail}</span>
          </div>
        ))}
        {failedChecks.length === 0 && r.totals.freeCardCustomers > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            {plural(r.totals.freeCardCustomers, 'customer')} had a FREE CARDS order, which counts as a freebie (Hitters).
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
        <button className="btn btn-primary" disabled={locked || r.hitters.pages === 0} onClick={() => downloadFile(batch.id, 'hitters')}>
          Download Hitters PDF ({plural(r.hitters.labels, 'label')})
        </button>
        <button className="btn btn-primary" disabled={locked || r.skunks.pages === 0} onClick={() => downloadFile(batch.id, 'skunks')}>
          Download Skunks PDF ({plural(r.skunks.labels, 'label')})
        </button>
        <button className="btn btn-secondary" onClick={() => downloadFile(batch.id, 'original')}>Original PDF</button>
      </div>
      {locked && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>Downloads unlock once the problems above are reviewed.</div>}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
        Each PDF is sorted A–Z by Buyer ID. A customer's labels are together, each followed by its packing slips.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, marginBottom: 8, flexWrap: 'wrap' }}>
        <div className="card-title" style={{ margin: 0 }}>Customers</div>
        {['all', 'hitters', 'skunks'].map(f => (
          <button key={f} className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => setFilter(f)}>
            {f === 'all' ? `All (${r.customers.length})` : f === 'hitters' ? `Hitters (${r.hitters.customers})` : `Skunks (${r.skunks.customers})`}
          </button>
        ))}
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Buyer ID</th>
              <th style={{ textAlign: 'left' }}>Pile</th>
              <th style={{ textAlign: 'left' }}>Why</th>
              <th style={{ textAlign: 'right' }}>Labels</th>
              <th style={{ textAlign: 'right' }}>Slips</th>
              <th style={{ textAlign: 'right' }}>Spots</th>
              <th style={{ textAlign: 'right' }}>Free cards</th>
              <th style={{ textAlign: 'left' }}>Pages</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={`${c.buyer_id}-${i}`}>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.buyer_id || <span style={{ color: 'var(--danger)' }}>Unknown</span>}</div>
                  {c.nickname && c.nickname !== c.buyer_id && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.nickname}</div>}
                </td>
                <td><span className={`badge ${c.pile === 'skunks' ? 'badge-default' : 'badge-success'}`}>{c.pile === 'skunks' ? 'Skunk' : 'Hitter'}</span></td>
                <td style={{ fontSize: 12 }}>{c.reason}</td>
                <td style={{ textAlign: 'right', fontWeight: c.labels > 1 ? 700 : 400 }}>{c.labels}</td>
                <td style={{ textAlign: 'right', fontWeight: c.slips > c.labels ? 700 : 400 }}>{c.slips}</td>
                <td style={{ textAlign: 'right' }}>{c.spots}</td>
                <td style={{ textAlign: 'right' }}>{c.free_cards || ''}</td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.pages.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Shipping() {
  const { hasPermission } = useAuth();
  if (!hasPermission('manage_shipping')) {
    return (
      <div>
        <h2 className="page-title">Shipping</h2>
        <div className="card">You don't have access to Shipping. Ask an admin to turn on the "manage_shipping" permission for your role.</div>
      </div>
    );
  }
  return <ShippingPage />;
}

function ShippingPage() {
  const [mode, setMode] = useState('date');
  const [date, setDate] = useState(todayET());
  const [dayBreaks, setDayBreaks] = useState([]);
  const [picked, setPicked] = useState({});   // id -> break, survives date changes
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [batch, setBatch] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/api/shipping/batches');
      setHistory(data.batches || []);
    } catch (err) {
      console.error('Load label batches error:', err);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    if (!date) return;
    api.get(`/api/breaks?startDate=${date}&endDate=${date}`)
      .then(({ data }) => setDayBreaks((data.breaks || []).filter(b => b.status !== 'cancelled')
        .sort((a, b) => (a.break_number ?? 0) - (b.break_number ?? 0))))
      .catch(() => setDayBreaks([]));
  }, [date]);

  const pickedList = Object.values(picked).sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.break_number ?? 0) - (b.break_number ?? 0));
  const togglePick = b => setPicked(prev => {
    const next = { ...prev };
    if (next[b.id]) delete next[b.id]; else next[b.id] = b;
    return next;
  });

  const submit = async () => {
    setError('');
    if (!file) { setError('Choose the label PDF first.'); return; }
    if (mode === 'breaks' && pickedList.length === 0) { setError('Tick at least one break.'); return; }
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    if (mode === 'date') form.append('date', date);
    else form.append('breakIds', pickedList.map(b => b.id).join(','));
    setUploading(true);
    try {
      const { data } = await api.post('/api/shipping/split', form);
      setBatch(data);
      setFile(null);
      setFileKey(k => k + 1);
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openBatch = async id => {
    try {
      const { data } = await api.get(`/api/shipping/batches/${id}`);
      setBatch(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not load that batch');
    }
  };

  const deleteBatch = async h => {
    if (!window.confirm(`Delete the upload "${h.original_filename}" (${h.scope_label}) from ${formatDateTime(h.created_at)}? Its split PDFs will be deleted too.`)) return;
    try {
      await api.delete(`/api/shipping/batches/${h.id}`);
      if (batch?.id === h.id) setBatch(null);
      loadHistory();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete that batch');
    }
  };

  const notFinal = (mode === 'date' ? dayBreaks : pickedList).filter(b => !['completed', 'pending'].includes(b.status));

  return (
    <div>
      <h2 className="page-title">Shipping</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>
        Upload the TikTok shipping label + packing slip PDF for break orders. Nucleus matches each packing slip's
        Buyer ID to the break customers and splits the labels into Hitters (any hit or freebie) and Skunks (on the skunk list).
      </p>

      <div className="card">
        <div className="card-title">Split a label PDF</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontSize: 14 }}>
          <span style={{ fontWeight: 600 }}>Check against:</span>
          <label style={inlineLabel}>
            <input type="radio" style={inlineInput} name="ship-scope" checked={mode === 'date'} onChange={() => setMode('date')} /> All breaks on a date
          </label>
          <label style={inlineLabel}>
            <input type="radio" style={inlineInput} name="ship-scope" checked={mode === 'breaks'} onChange={() => setMode('breaks')} /> Pick breaks
          </label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" style={{ width: 'auto' }} />
        </div>

        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
          {dayBreaks.length === 0 ? 'No breaks on this date.' : mode === 'date' ? (
            <>Breaks on this date: {dayBreaks.map(b => `#${b.break_number ?? b.id}`).join(', ')}</>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
              {dayBreaks.map(b => (
                <label key={b.id} style={inlineLabel}>
                  <input type="checkbox" style={inlineInput} checked={!!picked[b.id]} onChange={() => togglePick(b)} />
                  #{b.break_number ?? b.id} {b.name}
                </label>
              ))}
            </div>
          )}
        </div>
        {mode === 'breaks' && pickedList.length > 0 && (
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            <strong>{plural(pickedList.length, 'break')} picked:</strong> {pickedList.map(b => `#${b.break_number ?? b.id}`).join(', ')}{' '}
            <span style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setPicked({})}>clear</span>
          </div>
        )}
        {notFinal.length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 10 }}>
            ⚠ {notFinal.map(b => `#${b.break_number ?? b.id}`).join(', ')} {notFinal.length === 1 ? 'is' : 'are'} not completed yet. Hits there aren't final.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input key={fileKey} type="file" style={{ width: 'auto', flex: '1 1 240px', margin: 0 }} accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
          <button className="btn btn-primary" onClick={submit} disabled={uploading || !file}>
            {uploading ? 'Splitting…' : 'Upload & split'}
          </button>
        </div>
        {error && <div style={{ color: 'var(--danger)', marginTop: 10, fontWeight: 600 }}>{error}</div>}
      </div>

      {batch && <BatchReport key={batch.id} batch={batch} onAcknowledged={b => { setBatch(b); loadHistory(); }} />}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Past uploads</div>
        {history.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No label PDFs split yet.</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Uploaded</th>
                  <th style={{ textAlign: 'left' }}>File</th>
                  <th style={{ textAlign: 'left' }}>Scope</th>
                  <th style={{ textAlign: 'right' }}>Labels</th>
                  <th style={{ textAlign: 'right' }}>Hitters</th>
                  <th style={{ textAlign: 'right' }}>Skunks</th>
                  <th style={{ textAlign: 'right' }}>Skunk list</th>
                  <th style={{ textAlign: 'left' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} onClick={() => openBatch(h.id)} style={{ cursor: 'pointer', background: batch?.id === h.id ? 'var(--bg)' : undefined }}>
                    <td>{formatDateTime(h.created_at)}<div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{h.uploaded_by_name}</div></td>
                    <td style={{ fontSize: 12 }}>{h.original_filename}</td>
                    <td style={{ fontSize: 12 }}>{h.scope_label}</td>
                    <td style={{ textAlign: 'right' }}>{h.total_labels}</td>
                    <td style={{ textAlign: 'right' }}>{h.hitter_labels}</td>
                    <td style={{ textAlign: 'right' }}>{h.skunk_labels}</td>
                    <td style={{ textAlign: 'right', color: h.skunk_list_count === h.skunk_customers ? undefined : 'var(--danger)' }}>{h.skunk_list_count}</td>
                    <td>
                      {h.flag_count === 0
                        ? <span className="badge badge-success">OK</span>
                        : h.acknowledged_at
                          ? <span className="badge badge-warning">{plural(h.flag_count, 'flag')} · reviewed</span>
                          : <span className="badge badge-danger">{plural(h.flag_count, 'flag')}</span>}
                    </td>
                    <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteBatch(h)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
